import React, { useEffect, useState } from 'react';
import './Cart.scss';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import CartLineItem from './CartLineItem/CartLineItem';
import CartPrice from './CartPrice/CartPrice';
import { formatMoney } from '@shopify/theme-currency';
import { isEmpty } from '../../util/isEmpty';
import Loader from '../UI/Icons/Loader/Loader';
import Owl from '../UI/Graphics/Owl';
import { COLORS } from '../../constants/colors';
import CartUpsell from './CartUpsell/CartUpsell';

/**
 * Renders the cart component
 * @param {array} lineItems
 * @param {object} shoppingCart
 * @param {string} title
 * @param {string} subtitle
 * @param {string} totalPrice - Cart total price
 * @param {string} meta_info
 * @param {function} removeFromCart - Redux dispatch function to remove items to cart
 * @param {function} getCurrentCart - Redux dispatch function to get cart data
 * @param {function} updateCart - Redux dispatch function to update cart
 * @param {function} addToCart - Redux dispatch function to add items to cart
 * @param {boolean} show_upsell - Show/Hide upsell products
 * @param {array} upsell_products - Array with all upsell products
 * @param money_format
 * @returns {JSX.Element}
 * @constructor
 */
const Cart = ({
  lineItems,
  shoppingCart,
  title,
  subtitle,
  totalPrice,
  meta_info,
  removeFromCart,
  getCurrentCart,
  updateCart,
  addToCart,
  show_upsell,
  upsell_products,
  money_format,
}) => {
  const [fetching, setFetching] = useState(false);

  const fetchCart = async () => {
    setFetching(true);
    await getCurrentCart();
  };

  const addAction = async (product) => {
    setFetching(true);
    await addToCart({ quantity: 1, id: product.variants[0].id }, product.title)
      .then(() => setFetching(false))
      .catch((e) => {
        setFetching(false);
        console.error('Error fetching, please try again', e);
      });
  };

  useEffect(() => {
    fetchCart()
      .then(() => setFetching(false))
      .catch(() => {
        setFetching(false);
        console.error('Error fetching, please try again');
      });
  }, []);

  const { cart } = shoppingCart?.get || {};
  const formattedTotalPrice = formatMoney(cart.total_price, money_format);
  const price = isEmpty(cart) ? totalPrice : formattedTotalPrice;

  const renderLineItems = (data) => {
    if (data.length > 0) {
      return (
        <div className="c-cart__line-items">
          {data.map((product) => {
            return (
              <CartLineItem
                image={product.image}
                title={product.title}
                id={product.key}
                price={product.price}
                discounted_price={product.discounted_price}
                quantity={product.quantity}
                url={product.url}
                removeFromCart={removeFromCart}
                updateCart={updateCart}
                format={money_format}
              />
            );
          })}

          {show_upsell && (
            <div className="c-cart__upsell-container">
              {upsell_products.map((product) => (
                <CartUpsell product={product} addAction={addAction} format={money_format} />
              ))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <p className="c-cart__empty-cart body-2 color-black">
          {fetching ? <Loader /> : 'Your shopping cart is empty'}
        </p>
      );
    }
  };

  return (
    <div className="c-cart background-color-gray">
      <div className="c-cart__content">
        <div className="c-cart__header">
          <Owl color={COLORS.gray2} />
          <h2 className="c-cart__title h2-roobert color-black">{title}</h2>
          <div className="c-cart__subtitle body-1">
            <p>{subtitle}</p>
          </div>
        </div>
        {renderLineItems(lineItems)}
        <div className="c-cart__subtotal">
          <span className="c-cart__subtotal-label h4-roobert color-black">Subtotal</span>
          <span className="c-cart__subtotal-amount h4-roobert color-black">
            <CartPrice shoppingCart={shoppingCart} totalPrice={price} format={money_format} />
          </span>
        </div>
        <span className="c-cart__meta tag" dangerouslySetInnerHTML={{ __html: meta_info }}></span>
        <CTAButton
          target={'_self'}
          link={'/checkout'}
          text={`Checkout—${price}`}
          type={'primary'}
          additionalClasses={'cta'}
        />
      </div>
    </div>
  );
};

export default Cart;
